/**
 * A área de anexos do chamado (2026-10-20): arrastar, escolher no
 * computador ou colar um print (o Ctrl+V é tratado pelo formulário
 * inteiro, em `HelpCenterPage`). Imagem ganha miniatura; o resto, o ícone
 * do tipo.
 */

import { FileSpreadsheet, FileText, ImageIcon, Paperclip, X } from "lucide-react";
import { useEffect, useRef, useState, type DragEvent } from "react";
import { formatBytes, type AttachmentLimits } from "./support-attachments";

function AttachmentIcon({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = file.type.startsWith("image/");
  useEffect(() => {
    if (!isImage || typeof URL.createObjectURL !== "function") return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, isImage]);

  if (url) return <img className="help-attachment-thumb" src={url} alt="" />;
  const Icon = isImage ? ImageIcon : /\.(xlsx|csv)$/i.test(file.name) ? FileSpreadsheet : FileText;
  return (
    <span className="help-attachment-thumb" aria-hidden="true">
      <Icon size={16} />
    </span>
  );
}

export function SupportAttachments({
  files,
  limits,
  error,
  onAdd,
  onRemove
}: {
  files: File[];
  limits: AttachmentLimits;
  error: string | null;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const full = files.length >= limits.max;

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!full) onAdd(Array.from(event.dataTransfer.files));
  };

  return (
    <div className="help-attachments">
      <div className="help-field-head">
        <span className="help-attachments-label">
          Anexos <small>opcional</small>
        </span>
        <span className="help-counter">
          {files.length}/{limits.max}
        </span>
      </div>

      {!full && (
        <div
          className={`help-dropzone${dragging ? " is-dragging" : ""}`}
          onDragOver={event => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <Paperclip size={16} aria-hidden="true" />
          <span>
            Arraste arquivos, cole um print (<kbd>Ctrl</kbd> + <kbd>V</kbd>) ou{" "}
            <button type="button" className="text-button" onClick={() => inputRef.current?.click()}>
              escolha no computador
            </button>
          </span>
          <small>
            Imagem, PDF ou planilha · até {limits.max} arquivos de {formatBytes(limits.maxBytes)}
          </small>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            aria-label="Anexar arquivos"
            accept={limits.extensions.join(",")}
            onChange={event => {
              onAdd(Array.from(event.target.files ?? []));
              // Escolher o mesmo arquivo de novo depois de tirá-lo.
              event.target.value = "";
            }}
          />
        </div>
      )}

      {files.length > 0 && (
        <ul className="help-attachment-list">
          {files.map((file, index) => (
            <li key={`${file.name}-${file.size}`}>
              <AttachmentIcon file={file} />
              <span className="help-attachment-name" title={file.name}>
                {file.name}
              </span>
              <span className="help-attachment-size">{formatBytes(file.size)}</span>
              <button
                type="button"
                className="help-attachment-remove"
                onClick={() => onRemove(index)}
                aria-label={`Tirar ${file.name}`}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="help-attachment-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
